import { useState } from "react";
import { CircleUser, Download, KeyRound, ShieldCheck } from "lucide-react";

import { ErrorApi } from "../api/cliente.ts";
import { usarCambiarContrasena } from "../ganchos/datos.ts";
import { usarSesion } from "../sesion/ContextoSesion.tsx";
import { nombreRol, PERMISO, ROLES, puede } from "../sesion/permisos.ts";
import { BASE } from "../api/cliente.ts";
import { Aviso, Boton, Campo, Insignia, Seccion, Tarjeta } from "../componentes/ui.tsx";

const MINIMO = 12;

export function Cuenta() {
  const { rol, cuenta, puedo, salir } = usarSesion();
  const cambiar = usarCambiarContrasena();
  const [actual, setActual] = useState("");
  const [nueva, setNueva] = useState("");
  const [repetida, setRepetida] = useState("");
  const [listo, setListo] = useState(false);

  const noCoincide = repetida.length > 0 && nueva !== repetida;
  const corta = nueva.length > 0 && nueva.length < MINIMO;

  function enviar(e: React.FormEvent) {
    e.preventDefault();
    if (noCoincide || corta) return;
    cambiar.mutate(
      { actual, nueva },
      {
        onSuccess: () => {
          setListo(true);
          setActual("");
          setNueva("");
          setRepetida("");
          // Cambiar la contraseña cierra todas las sesiones en el servidor,
          // incluida la de quien la hubiera robado. Quedarse aquí con una
          // sesión ya muerta solo produciría 401 confusos.
          setTimeout(() => void salir(), 2500);
        },
      },
    );
  }

  const misPermisos = ROLES.find((r) => r.valor === rol);

  return (
    <Seccion titulo="Mi cuenta" descripcion="Quién eres aquí dentro y qué puedes hacer.">
      <div className="grid gap-6 lg:grid-cols-2">
        <Tarjeta className="space-y-5 p-6">
          <div className="flex items-start gap-3">
            <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-marca-suave text-marca-tinta">
              <CircleUser className="size-4.5" aria-hidden />
            </span>
            <div>
              <h2 className="font-semibold tracking-tight">Tu acceso</h2>
              <p className="mt-0.5 text-sm text-tenue">
                El rol sale del token verificado, no de esta pantalla.
              </p>
            </div>
          </div>

          <dl className="space-y-3 text-sm">
            <div className="flex items-center justify-between gap-4">
              <dt className="text-tenue">Cuenta</dt>
              <dd className="font-medium">{cuenta ?? "—"}</dd>
            </div>
            <div className="flex items-center justify-between gap-4">
              <dt className="text-tenue">Rol</dt>
              <dd>
                <Insignia tono="marca">{nombreRol(rol)}</Insignia>
              </dd>
            </div>
          </dl>

          {misPermisos && (
            <p className="rounded-xl bg-superficie-2 p-3 text-sm leading-relaxed text-tenue">
              {misPermisos.descripcion}
            </p>
          )}

          <ul className="space-y-2 text-sm">
            {(
              [
                [PERMISO.VER_PAGOS, "Ver los pagos"],
                [PERMISO.DECIDIR, "Confirmar y descartar"],
                [PERMISO.VER_CARTERA, "Ver la cartera"],
                [PERMISO.EXPORTAR, "Exportar"],
                [PERMISO.INVITAR, "Invitar personas"],
                [PERMISO.MOVER_AUTONOMIA, "Decidir cuánto resuelve 4drando solo"],
              ] as const
            ).map(([permiso, texto]) => (
              <li key={permiso} className="flex items-center gap-2.5">
                <ShieldCheck
                  className={puedo(permiso) ? "size-4 text-exito" : "size-4 text-tenue-2 opacity-40"}
                  aria-hidden
                />
                <span className={puedo(permiso) ? "text-tinta" : "text-tenue-2 line-through"}>
                  {texto}
                </span>
              </li>
            ))}
          </ul>
        </Tarjeta>

        <Tarjeta className="space-y-5 p-6">
          <div className="flex items-start gap-3">
            <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-dato-suave text-dato-tinta">
              <KeyRound className="size-4.5" aria-hidden />
            </span>
            <div>
              <h2 className="font-semibold tracking-tight">Cambiar contraseña</h2>
              <p className="mt-0.5 text-sm text-tenue">
                Cambiarla cierra todas tus sesiones abiertas — incluida esta.
              </p>
            </div>
          </div>

          {listo ? (
            <Aviso tono="exito" titulo="Contraseña cambiada" icono={ShieldCheck}>
              Todas las sesiones quedaron cerradas. Te llevamos de vuelta al inicio para que entres
              con la nueva.
            </Aviso>
          ) : (
            <form className="space-y-4" onSubmit={enviar}>
              <Campo
                etiqueta="Contraseña actual"
                type="password"
                autoComplete="current-password"
                required
                value={actual}
                onChange={(e) => setActual(e.target.value)}
              />
              <Campo
                etiqueta="Nueva contraseña"
                type="password"
                autoComplete="new-password"
                required
                minLength={MINIMO}
                value={nueva}
                onChange={(e) => setNueva(e.target.value)}
                ayuda={`Mínimo ${MINIMO} caracteres. Una frase larga es más segura y más fácil de recordar que un jeroglífico corto.`}
                error={corta ? `Todavía le faltan ${MINIMO - nueva.length} caracteres.` : undefined}
              />
              <Campo
                etiqueta="Repite la nueva"
                type="password"
                autoComplete="new-password"
                required
                value={repetida}
                onChange={(e) => setRepetida(e.target.value)}
                error={noCoincide ? "No coinciden." : undefined}
              />

              {cambiar.error && (
                <Aviso titulo="No se pudo cambiar">
                  {cambiar.error instanceof ErrorApi
                    ? cambiar.error.message
                    : "Error al hablar con 4drando."}
                </Aviso>
              )}

              <Boton
                type="submit"
                variante="primario"
                cargando={cambiar.isPending}
                disabled={!actual || nueva.length < MINIMO || noCoincide}
              >
                Cambiar contraseña
              </Boton>
            </form>
          )}
        </Tarjeta>

        {puede(rol, PERMISO.EXPORTAR) && <Exportar />}
      </div>
    </Seccion>
  );
}

/**
 * Llevarse los datos (SPEC-007 §5 y CA-7).
 *
 * Esto compensa haber dejado la hoja de cálculo. La promesa —"los datos siguen
 * siendo tuyos"— solo vale si se puede ejercer sin pedírnosla a nosotros, así
 * que es un enlace visible y no un correo de soporte.
 *
 * Son enlaces `<a>` normales, no `fetch`: la cookie de sesión viaja sola, el
 * navegador respeta el `Content-Disposition` y el archivo nunca pasa por la
 * memoria de esta pestaña. Un export de veinte megas por `fetch` sí lo haría.
 */
const TABLAS = [
  ["movimientos", "Pagos del banco"],
  ["abonos", "Pagos registrados"],
  ["facturas", "Facturas"],
  ["decisiones", "Decisiones"],
  ["correos", "Correos recibidos"],
] as const;

function Exportar() {
  return (
    <Tarjeta className="space-y-5 p-6">
      <div className="flex items-start gap-3">
        <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-marca-suave text-marca-tinta">
          <Download className="size-4.5" aria-hidden />
        </span>
        <div>
          <h2 className="font-semibold tracking-tight">Llevarte tus datos</h2>
          <p className="mt-0.5 max-w-prose text-sm text-tenue">
            Son tuyos y te los puedes llevar cuando quieras, sin pedírnoslo. Nada se borra de aquí
            al hacerlo.
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium">Para abrir en Excel</p>
        <div className="flex flex-wrap gap-2">
          {TABLAS.map(([tabla, nombre]) => (
            <a
              key={tabla}
              href={`${BASE}/api/v1/exportar?formato=csv&tabla=${tabla}`}
              className="rounded-xl border border-borde px-3 py-1.5 text-sm font-medium text-tenue transition-colors hover:bg-superficie-2 hover:text-tinta"
            >
              {nombre}
            </a>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium">Todo de una vez</p>
        <a
          href={`${BASE}/api/v1/exportar`}
          className="inline-flex items-center gap-1.5 rounded-xl border border-borde px-3 py-1.5 text-sm font-medium text-tenue transition-colors hover:bg-superficie-2 hover:text-tinta"
        >
          <Download className="size-4" aria-hidden />
          Descargar en JSON
        </a>
        <p className="max-w-prose text-xs text-tenue-2">
          El JSON lleva <strong>todas</strong> las tablas, para que otro sistema pueda leerlas. Los
          CSV llevan una cada uno, que es lo que se abre y se filtra. Ni uno ni otro incluyen las
          claves de conexión: esas son del servidor, no tuyas.
        </p>
      </div>
    </Tarjeta>
  );
}
